import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { OverviewTodo, PilotPill, TodoRow } from '@aithora/shared';
import type { QueryState } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { todoNote } from './TodoTab';
import { familyCheckins, teamMemoFeed, type FamilyCheckins, type TeamMemoFeed } from './v9Sources';

/**
 * The V9 blocks at the top of the Facility Admin's Overview (design isPlatform for Facility
 * Admin, L5337-5436): what needs them today, the team memo and the family check-ins. The
 * setup checklist and "Is Aithora working" sit between them, as in the design.
 */

const CARD = 'display:flex;flex-direction:column;gap:2px;margin:10px 0;padding:14px 16px;border-radius:12px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;';
const PRIMARY = 'display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;border:0;font-family:inherit;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;cursor:pointer;';
const ROW = 'display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);';

/** The rows that need the admin today, most urgent first, as the server ordered them. */
export function todayRows(d: OverviewTodo | null | undefined, max = 3): TodoRow[] {
  if (!d) return [];
  return d.rows.filter((r) => r.group === 'now' || r.group === 'today').slice(0, max);
}

/** "3 things need you today" / "1 thing needs you today" / "Nothing needs you today". */
export function todayHead(n: number): string {
  if (n === 0) return 'Nothing needs you today';
  return n === 1 ? '1 thing needs you today' : `${n} things need you today`;
}

/** Design "Needs you today" (faTop, L40721): up to three rows, each with its one action, and Triage. */
export function NeedsYouToday({ todo, onTriage, onTodo }: { todo: QueryState<OverviewTodo>; onTriage: () => void; onTodo: () => void }) {
  const rows = todayRows(todo.data);
  const now = Date.now();
  if (todo.loading && !todo.data) return null;
  return (
    <section data-screen-label="Needs you today" data-testid="needs-you-today" aria-label="Needs you today" style={sx(CARD)}>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:4px;')}>
        <h2 style={sx('flex:1 1 auto;margin:0;font-size:19px;font-weight:800;color:#22272E;')}>{todayHead(rows.length)}</h2>
        <button type="button" onClick={onTriage} style={sx(PRIMARY)}>
          Triage
        </button>
      </div>
      {todo.error && !todo.data && (
        <div style={sx(ROW)}>
          <span style={sx('flex:1 1 300px;min-width:0;font-size:16px;color:#54606C;line-height:1.4;')}>Your list could not be loaded. Open Triage to see what is waiting.</span>
        </div>
      )}
      {todo.data && rows.length === 0 && (
        <div style={sx(ROW)}>
          <span style={sx('flex:1 1 300px;min-width:0;font-size:16px;color:#54606C;line-height:1.4;')}>
            Nothing is waiting on you. New flags land in Triage first; open it to place them.
          </span>
        </div>
      )}
      {rows.map((r) => {
        const btn = r.action.label || 'Open';
        return (
          <div key={r.id} style={sx(ROW)}>
            <div style={sx('flex:1 1 300px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{r.title}</span>
              <span style={sx('font-size:16px;color:#54606C;line-height:1.4;')}>{todoNote(r, now)}</span>
            </div>
            {r.action.to ? (
              <Link to={r.action.to} style={sx(PRIMARY)}>
                {btn}
              </Link>
            ) : (
              // An action taken in place (claim, assign, call) runs from the To do list.
              <button type="button" onClick={onTodo} style={sx(PRIMARY)}>
                {btn}
              </button>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** Design Team memo (memoVals, L40014): what the admin sent staff, and who read it. */
export function TeamMemoCard({ feed = teamMemoFeed() }: { feed?: TeamMemoFeed }) {
  return (
    <section data-screen-label="Team memo" data-testid="team-memo" aria-label="Team memo" style={sx('flex:0 0 auto;margin-bottom:9px;border:1px solid rgba(34,39,46,.12);border-radius:10px;background:#FCFAF4;padding:12px 16px;display:flex;flex-direction:column;gap:6px;')}>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;')}>Team memo</span>
        <span style={sx('font-size:16px;color:#54606C;')}>Staff see it at the top of their Worklist and tap Got it.</span>
      </div>
      {!feed.connected ? (
        <div data-testid="team-memo-empty" style={sx('display:flex;flex-direction:column;gap:2px;padding:8px 0 2px;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>No memos yet</span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;max-width:90ch;')}>
            Sending a memo to staff is not switched on for your building yet. Nothing written here would reach anyone, so there is no box to write in until it is.
          </span>
        </div>
      ) : feed.memos.length === 0 ? (
        <div style={sx('padding:8px 0 2px;border-top:1px solid rgba(34,39,46,.08);font-size:16px;color:#54606C;')}>No memos sent yet.</div>
      ) : (
        feed.memos.map((m) => (
          <div key={m.id} style={sx('display:flex;gap:6px 12px;flex-wrap:wrap;align-items:baseline;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);')}>
            <span style={sx('font-size:16px;color:#22272E;line-height:1.4;flex:1 1 300px;min-width:0;')}>{m.text}</span>
            <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
              {m.to} {'·'} {m.sentAt} {'·'} <b style={sx('color:#22272E;')}>{`${m.read} of ${m.of} read`}</b>
            </span>
          </div>
        ))
      )}
    </section>
  );
}

/** Design Family check-ins this week (fcVals, L51023): the weekly one-question check-in. */
export function FamilyCheckinsBlock({ data = familyCheckins() }: { data?: FamilyCheckins }) {
  const [called, setCalled] = useState<Record<string, boolean>>({});
  return (
    <section data-screen-label="Family check-ins" data-testid="family-checkins" aria-label="Family check-ins this week" style={sx('box-sizing:border-box;padding:0 0 12px;margin-bottom:12px;border-bottom:1px solid rgba(34,39,46,.22);display:flex;flex-direction:column;gap:4px;min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:800;')}>Family check-ins this week</span>
        {data.connected && data.answered !== null && data.families !== null && (
          <span style={sx('font-size:16px;color:#54606C;')}>{`${data.answered} of ${data.families} families answered`}</span>
        )}
      </div>
      {!data.connected ? (
        <>
          <span data-testid="family-checkins-empty" style={sx('font-size:16px;color:#3C454F;line-height:1.45;max-width:90ch;')}>
            No answers yet. Families get one question a week in the Family Portal, and a low answer comes here with a call-back deadline. The weekly question is not switched on for your building yet.
          </span>
          <span style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:2px;')}>
            <Link to="/family" style={sx('display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.4);')}>
              Open the Family Portal
            </Link>
          </span>
        </>
      ) : (
        <>
          {data.trend && <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{data.trend}</span>}
          {data.rows.map((r) => {
            const done = r.calledBack || called[r.id];
            return (
              <div key={r.id} style={sx('display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;padding:7px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                <div style={sx('flex:1 1 320px;min-width:0;display:flex;flex-direction:column;gap:1px;')}>
                  <span style={sx('font-size:16px;font-weight:700;')}>{r.who}</span>
                  <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{r.said}</span>
                  {!done && r.callBackBy && <span style={sx('font-size:14px;color:#B3261E;font-weight:700;')}>{r.callBackBy}</span>}
                </div>
                {done ? (
                  <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>Called back</span>
                ) : (
                  <button type="button" onClick={() => setCalled((c) => ({ ...c, [r.id]: true }))} style={sx('display:inline-flex;align-items:center;min-height:36px;padding:6px 12px;border:0;border-radius:8px;background:#2F5C97;color:#fff;font-family:inherit;font-size:16px;font-weight:700;white-space:nowrap;cursor:pointer;')}>
                    Call back
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

/**
 * The setup checklist's pilot pill (design fsPilot, L47528): "90-DAY FREE PILOT · DAY 64 OF
 * 90 · FOUNDING50". The day appears once the building is live; the code when one is on file.
 */
export function pilotPill(p: PilotPill | null | undefined): { text: string; tip: string } | null {
  if (!p || (!p.code && p.day === null)) return null;
  const days = p.pilotDays ?? 90;
  const text = [`${days}-DAY FREE PILOT`, p.day !== null ? `DAY ${Math.min(p.day, days)} OF ${days}` : null, p.code].filter(Boolean).join(' · ');
  const tip = p.code
    ? `Code ${p.code} is on your contract. The pilot is free for ${days} days from go-live, and you pay $0 until it converts.`
    : `The pilot is free for ${days} days from go-live, and you pay $0 until it converts.`;
  return { text, tip };
}
